import { useState, useCallback, useRef } from "react";

import type { UseCachedOptionsResult } from "./types";

/**
 * Hook to manage cached options found by the lookup tool for the select tool to use.
 * Shared between BrazeSelect and BrazeSelectAsyncCreatable.
 *
 * @param formatOptionStringLabelRef - Ref to function that extracts a string label from an option
 */
export const useCachedOptions = <OptionType>(
  formatOptionStringLabelRef: React.MutableRefObject<(option: OptionType) => string>
): UseCachedOptionsResult<OptionType> => {
  const [cachedOptions, setCachedOptions] = useState<Record<string, OptionType>>({});
  const cachedOptionsRef = useRef(cachedOptions);
  cachedOptionsRef.current = cachedOptions;

  const updateCachedOptions = useCallback(
    (options: OptionType[]) => {
      const newOptions: Record<string, OptionType> = {};
      options.forEach((option) => {
        const key = formatOptionStringLabelRef.current(option);
        newOptions[key] = option;
      });
      setCachedOptions((prev) => ({ ...prev, ...newOptions }));
    },
    [formatOptionStringLabelRef]
  );

  return {
    cachedOptions,
    cachedOptionsRef,
    updateCachedOptions,
  };
};
